import { useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ContestContainer.css";
import { useSelector } from "react-redux";
import actions from "../../redux/actions";
import TeamDetailsModal, { useTeamDetailsModal } from "./TeamDetailsModal";
import NavbarSidebarWrapper from "../navbarSidebarWrapper";
import { useParams } from "react-router-dom";

export default function CompleteContestContainer() {
  const { matchId } = useParams();

  const completedContests = useSelector(
    (state) => state.fantacySlice?.FnCompletedContestList?.Result,
  );

  const userContestsRaw = useSelector(
    (state) => state.fantacySlice?.FnUserContestList?.Result,
  );
  const userContests = Array.isArray(userContestsRaw) ? userContestsRaw : [];

  const { showTeamModal, teamDetails, loadingTeam, teamTitle, openTeamModal, closeTeamModal } = useTeamDetailsModal(matchId);

  useEffect(() => {
    if (matchId) {
      actions.getCompletedContestList(matchId).catch((err) => {
        console.error("Completed contest API error:", err);
      });
      actions.getUserContestList(matchId).catch((err) => {
        console.error("User contest API error:", err);
      });
    }
  }, [matchId]);

  // TeamIds the loggedin user joined across all contests
  const myTeamIds = useMemo(() => {
    return new Set(userContests.map((uc) => String(uc.TeamId)));
  }, [userContests]);

  // Map contestId (as string) → Set of users TeamIds for that contest
  const myTeamIdsByContest = useMemo(() => {
    const map = {};
    userContests.forEach((uc) => {
      const key = String(uc.ContestId);
      if (!map[key]) map[key] = new Set();
      map[key].add(String(uc.TeamId));
    });
    return map;
  }, [userContests]);

  // Set of contest IDs the user has joined
  const joinedContestIds = useMemo(() => {
    return new Set(userContests.map((uc) => String(uc.ContestId)));
  }, [userContests]);

  const groupedContests = useMemo(() => {
    if (!Array.isArray(completedContests)) return [];
    const map = {};
    completedContests.forEach((item) => {
      const key = String(item.contest_id);
      if (!map[key]) {
        map[key] = {
          contest_id: item.contest_id,
          ContestName: item.ContestName,
          entry_fee: item.entry_fee,
          TotalPool: item.TotalPool,
          teams: [],
        };
      }
      map[key].teams.push({
        TeamId: String(item.TeamId),
        UserName: item.UserName,
        Rank: item.Rank,
        TotalWinning: item.TotalWinning,
      });
    });

    // Inject user's teams from userContestList if not already in the rank list
    userContests.forEach((uc) => {
      const key = String(uc.ContestId);
      if (map[key]) {
        const existingTeamIds = new Set(map[key].teams.map((t) => t.TeamId));
        if (!existingTeamIds.has(String(uc.TeamId))) {
          map[key].teams.push({
            TeamId: String(uc.TeamId),
            UserName: uc.UserName || uc.TeamName || `Team ${uc.TeamId}`,
            Rank: uc.Rank || map[key].teams.length + 1,
            TotalWinning: uc.TotalWinning || 0,
          });
        }
      }
    });

    Object.values(map).forEach((contest) => {
      contest.teams.sort((a, b) => a.Rank - b.Rank);
    });
    return Object.values(map);
  }, [completedContests, userContests]);

  const renderTeamRows = (contest) => {
    const contestTeamIds = myTeamIdsByContest[String(contest.contest_id)] || new Set();

    return (
      <>
        {contest.teams.map((team, idx) => {
          const isMyTeam = contestTeamIds.has(String(team.TeamId));
          return (
            <tr
              key={idx}
              style={{
                cursor: "pointer",
                backgroundColor: isMyTeam ? "rgba(0, 123, 255, 0.25)" : undefined,
              }}
              onClick={() => openTeamModal(team.TeamId, team.UserName || `Team ${team.TeamId}`)}
            >
              <td>#{team.Rank}</td>
              <td className={isMyTeam ? "text-warning fw-bold" : "text-info"}>
                {team.UserName || `Team ${team.TeamId}`}
                {isMyTeam && " (You)"}
              </td>
              <td className="text-success fw-bold">
                {(() => {
                  const pointsText = team.TotalWinning === 0 ? "View Points" : `Points ${Number(team.TotalWinning).toLocaleString()}`;
                  return pointsText;
                })()}
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <NavbarSidebarWrapper>
      <div className="content-wrapper me-0">
        <div className="container mt-4">
          <h3 className="mb-3 text-primary">Completed Contests</h3>

          {!groupedContests.length ? (
            <p className="text-white-50 text-center mt-4">No completed contests found.</p>
          ) : (
            <div className="row g-4">
              {groupedContests.map((contest) => (
                <div key={contest.contest_id} className="col-md-4 col-sm-6">
                  <div className="contest-card shadow-sm position-relative">
                    <span className="contest-name">{contest.ContestName}</span>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="contest-price">
                        Pool Point {Number(contest.TotalPool).toLocaleString()}
                      </span>
                      <span className="contest-entry">
                        Entry <b>Point {contest.entry_fee}</b>
                      </span>
                    </div>

                    <table className="table table-sm table-dark table-borderless mt-3 mb-0">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Team</th>
                          <th>Winning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderTeamRows(contest)}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <TeamDetailsModal
        show={showTeamModal}
        onHide={closeTeamModal}
        title={teamTitle}
        loading={loadingTeam}
        players={teamDetails}
      />
    </NavbarSidebarWrapper>
  );
}
